import { navigate } from 'gatsby'
import React, { ReactNode, useContext } from 'react'

import { LocaleContext, MeasurLocale, TranslationKey } from '../locale/i18n'
import { staticPagePaths } from '../locale/pathing'
import { StaticPage } from '../logic/staticPage'
import { isExternal } from '../logic/url'

import Button from './Button'

import './HeroCTA.scss'

export enum CTAAction {
  BLOG = 'blog',
  CONTACT_ANCHOR = 'contact',
  CONTACT_PAGE = 'contact-page',
  PARTNER = 'become-partner',
  OFFER = 'get-offer',
  SHOP = 'shop',
  SOLUTIONS = 'solutions',
}

const buttonTextKeyByAction: Record<CTAAction, TranslationKey> = {
  [CTAAction.BLOG]: 'common.goToBlog',
  [CTAAction.CONTACT_ANCHOR]: 'common.contactUs',
  [CTAAction.CONTACT_PAGE]: 'common.contactUs',
  [CTAAction.OFFER]: 'common.askForOffer',
  [CTAAction.PARTNER]: 'common.partner',
  [CTAAction.SHOP]: 'common.goToShop',
  [CTAAction.SOLUTIONS]: 'common.goToSolutions',
}

const targetByAction: Record<
  CTAAction,
  (params: { locale: MeasurLocale; searchParams?: string }) => string
> = {
  [CTAAction.BLOG]: ({ locale }) => staticPagePaths[StaticPage.BLOG][locale],
  [CTAAction.CONTACT_ANCHOR]: () => `${location.pathname}#askForOffer`,
  [CTAAction.CONTACT_PAGE]: ({ locale }) =>
    staticPagePaths[StaticPage.CONTACT][locale],
  [CTAAction.OFFER]: () => `${location.pathname}#askForOffer`,
  [CTAAction.PARTNER]: () => `${location.pathname}#becomePartner`,
  [CTAAction.SHOP]: ({ locale, searchParams }) =>
    `${staticPagePaths[StaticPage.PRODUCTS][locale]}${searchParams ?? ''}`,
  [CTAAction.SOLUTIONS]: ({ locale }) =>
    staticPagePaths[StaticPage.SOLUTIONS][locale],
}

export const ActionButton = ({
  action,
  role = 'primary',
  searchParams,
  text,
}: {
  action: CTAAction
  role: 'primary' | 'secondary'
  searchParams?: string
  text?: ReactNode
}) => {
  const { t, locale } = useContext(LocaleContext)

  return (
    <Button
      size="xl"
      onClick={e => {
        e.currentTarget.blur()
        void navigate(targetByAction[action]({ locale, searchParams }))
      }}
      variant={role === 'secondary' ? 'outline' : 'solid'}
    >
      {text || t(buttonTextKeyByAction[action])}
    </Button>
  )
}

const HeroCTA = ({
  ctaAction,
  ctaAction2,
  ctaCustomLink,
  ctaCustomText,
  ctaSearchParams,
}: {
  ctaAction: CTAAction
  ctaAction2?: CTAAction
  ctaCustomText?: string
  ctaCustomLink?: string
  ctaSearchParams?: string
}) => {
  const { t, locale } = useContext(LocaleContext)

  return (
    <div className="heroCta">
      <div className="heroCta__buttons">
        {ctaCustomLink || ctaCustomText ? (
          <Button
            size="xl"
            onClick={() => {
              if (isExternal(ctaCustomLink)) {
                window.open(ctaCustomLink, '_blank')
                return
              }
              window.location.href =
                ctaCustomLink ||
                targetByAction[ctaAction]({
                  locale,
                  searchParams: ctaSearchParams,
                })
            }}
          >
            {ctaCustomText || t(buttonTextKeyByAction[ctaAction])}
          </Button>
        ) : (
          <>
            {ctaAction && (
              <ActionButton
                action={ctaAction}
                role="primary"
                searchParams={ctaSearchParams}
              />
            )}
            {ctaAction2 && (
              <ActionButton
                action={ctaAction2}
                role="secondary"
                searchParams={ctaSearchParams}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default HeroCTA
